import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { FaGlobe, FaArrowLeft, FaTimes } from "react-icons/fa";
import { useLanguage } from "../../components/LanguageContext";
import {
  ClickToPath,
  Language,
  PackageContent,
  PackageContentPatch,
} from "../../Types/types";
import { addpackagetranslation } from "./addpackagetranslation";
import { AddLanguageModal } from "../../components/shared/AddLanguageModal";
import PackageListHeader from "../../components/Headers/PackageListHeader";
import RotationInput from "./RotationInput";
import PanoramaInputs from "./PanoramaInputs";
import { useDispatch, useSelector } from "react-redux";
import {
  AppDispatch,
  RootState,
} from "../../components/Manual_List/fetch/store";
import {
  fetchContentTypes,
  fetchLinkForPanoramaEdit,
  fetchStepById,
  fetchStepThumbnail,
  uploadContent,
  uploadContentThumbnail,
} from "../../components/Manual_List/fetch/slices";
import {
  createStepDataPayload,
  createPackageContentPatchPayload,
  handlePatchPackageContent,
  handlePatchLinks,
} from "../../utils/submitHandlers";
import { secondaryFiletrnslts } from "./secondaryFiletrnslts";
import { deleteContentThumbnail } from "../../components/Manual_List/fetch/slices";

const EditStep: React.FC = () => {
  const { language } = useLanguage();
  const t = addpackagetranslation[language as Language];
  const t2 = secondaryFiletrnslts[language as Language];

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const state = location.state || {};
  const { id } = useParams<{ id: string }>();
  const packageId = state.packageId;
  const [stepNames, setStepNames] = useState<Record<string, string>>({});
  const [stepType, setStepType] = useState<string>("");
  const [stepFile, setStepFile] = useState<File | null>(null);
  const [secondaryFile, setSecondaryFile] = useState<File | null>(null);
  const [stepNumbers, setStepNumbers] = useState<number[]>([]);
  const [stepRotation, setStepRotation] = useState<number>(0);
  const [stepInitialImagePath, setStepInitialImagePath] = useState<string>("");
  const [stepClickToPaths, setStepClickToPaths] = useState<ClickToPath[]>([]);
  const [isStepNameModalOpen, setIsStepNameModalOpen] = useState(false);
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [mainFileUrl, setMainFileUrl] = useState<string | null>(null);
  const [secondaryFileUrl, setSecondaryFileUrl] = useState<string | null>(null);
  const [dragTarget, setDragTarget] = useState<string | null>(null);
  const contentTypes = useSelector(
    (state: RootState) => state.data.contentTypes.data
  );
  const links = useSelector((state: RootState) => state.data.link.data);

  useEffect(() => {
    const fetchData = async () => {
      dispatch(fetchContentTypes(language));

      if (id) {
        await Promise.all([
          dispatch(fetchStepById({ id })),
          dispatch(fetchStepThumbnail({ id })),
        ]).then(([stepResult, thumbnailResult]) => {
          if (thumbnailResult.meta.requestStatus === "fulfilled") {
            const payload = thumbnailResult.payload as {
              thumbnailUrl?: string;
            };
            let thumbnailUrl = payload?.thumbnailUrl ?? "";
            setThumbnailUrl(thumbnailUrl);
          }

          return dispatch(
            fetchLinkForPanoramaEdit({
              packageId,
              packageContentId: Number(id),
            })
          );
        });
      }
    };

    fetchData();
  }, [dispatch, language, id, packageId]);

  useEffect(() => {
    if (location.state && location.state.steps) {
      setStepNumbers(location.state.steps);
    }
  }, [location.state]);

  const stepData = useSelector((state: RootState) => state.data.step.data);

  useEffect(() => {
    if (stepData) {
      const labels: Record<string, string> =
        typeof stepData.label === "string"
          ? { [language]: stepData.label }
          : stepData.label;
      setStepNames(labels);

      const typeWithName = stepData.type as unknown as { name: string };

      setStepType(typeWithName.name);
      const settings = stepData.settings ? JSON.parse(stepData.settings) : {};
      setStepRotation(
        typeWithName.name === "object" ? settings.rotation || 0 : 0
      );
      setStepInitialImagePath(
        typeWithName.name === "panorama" ? settings.initialImagePath || "" : ""
      );
      setStepClickToPaths(
        stepData.links.map((link) => ({
          path: link.targetPackageContentId,
          angle: parseFloat(link.settings),
        }))
      );
    }
  }, [stepData, language]);

  useEffect(() => {
    setStepFile(null);
    setMainFileUrl(null);
  }, [stepType]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validStepTypes = [
      "object",
      "image",
      "panorama",
      "pdf",
      "video",
    ] as const;
    type StepType = (typeof validStepTypes)[number];

    if (!validStepTypes.includes(stepType as StepType)) {
      console.error("Invalid step type:", stepType);
      return;
    }

    if (!stepData || !stepData.links) {
      console.error("stepData or stepData.links is undefined");
      return;
    }

    const currentStepNumber = stepNumbers.find(
      (stepNumber) => stepNumber === stepData.step
    );
    if (currentStepNumber === undefined) {
      console.error("Current step number not found");
      return;
    }

    const stepDataPayload = {
      ...createStepDataPayload(
        id!,
        stepNames,
        language,
        stepRotation,
        stepInitialImagePath,
        stepClickToPaths,
        stepType,
        contentTypes,
        stepFile,
        stepData
      ),
      step: currentStepNumber,
    };

    const payload: PackageContentPatch = createPackageContentPatchPayload(
      stepDataPayload,
      stepNames
    );

    await handlePatchPackageContent(
      dispatch,
      stepDataPayload,
      payload as unknown as PackageContent
    );

    if (stepType === "panorama") {
      await handlePatchLinks(dispatch, stepClickToPaths, stepData, language);
    }

    if (stepFile) {
      await dispatch(
        uploadContent({
          id: id!,
          primaryFile: stepFile,
          secondaryFile: secondaryFile || undefined,
        })
      ).unwrap();
    }

    if (thumbnail) {
      await dispatch(uploadContentThumbnail({ id: Number(id), thumbnail }));
    }

    navigate(`/steps-list/${packageId}`, { state });
  };

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setFile: React.Dispatch<React.SetStateAction<File | null>>,
    setUrl: React.Dispatch<React.SetStateAction<string | null>>
  ) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFile(file);
      setUrl(URL.createObjectURL(file));
    }
  };

  const handleRemoveFile = async (
    setFile: React.Dispatch<React.SetStateAction<File | null>>,
    setUrl: React.Dispatch<React.SetStateAction<string | null>>,
    e: React.MouseEvent,
    isThumbnail?: boolean
  ) => {
    e.stopPropagation();
    setFile(null);
    setUrl(null);
    if (isThumbnail && id) {
      await dispatch(deleteContentThumbnail(Number(id)));
    }
  };

  const capitalizeFirstLetter = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (dragTarget === "thumbnail") {
        setThumbnail(file);
        setThumbnailUrl(URL.createObjectURL(file));
      } else if (dragTarget === "mainFile") {
        setStepFile(file);
        setMainFileUrl(URL.createObjectURL(file));
      }
      setDragTarget(null);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div className="min-h-screen w-screen flex flex-col bg-gray-100 dark:bg-darkBackground dark:text-white">
      <PackageListHeader />
      <div className="flex flex-col items-center justify-start flex-grow">
        <form
          id="add-steps-form"
          className="w-full px-4 sm:px-6 md:px-8 py-6 max-w-7xl flex flex-col relative"
          onSubmit={handleSubmit}
        >
          <div className="flex items-center mb-6">
            <FaArrowLeft
              className="text-gray-600 dark:text-gray-400 cursor-pointer mr-3 hover:text-purple dark:hover:text-purple transition-colors"
              onClick={() => navigate(`/steps-list/${packageId}`, { state })}
              size={20}
            />
            <h1 className="text-2xl font-semibold">{t.addStep}</h1>
          </div>
          <div className="mb-4 relative">
            <label htmlFor="name" className="block mb-2 text-base font-medium ml-2">
              {t.name}
            </label>
            <div className="relative">
              <input
                type="text"
                id="name"
                className="w-full pl-4 pr-12 py-3 rounded-lg border border-gray-300 shadow-sm focus:outline-none focus:border-purple focus:ring-1 focus:ring-purple dark:bg-darkBackground dark:border-gray-600 dark:focus:border-purple transition-colors cursor-pointer"
                value={stepNames[language] || ""}
                onChange={(e) =>
                  setStepNames({ ...stepNames, [language]: e.target.value })
                }
                onClick={() => setIsStepNameModalOpen(true)}
                autoComplete="off"
              />
              <FaGlobe className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
            </div>
          </div>
          <div className="mb-4 relative">
            <label htmlFor="type" className="block mb-2 text-base font-medium ml-2">
              {t.type}
            </label>
            <div className="relative">
              <select
                id="type"
                className="w-full pl-4 pr-4 py-3 rounded-lg border border-gray-300 shadow-sm focus:outline-none focus:border-purple focus:ring-1 focus:ring-purple dark:bg-darkBackground dark:border-gray-600 dark:focus:border-purple transition-colors cursor-pointer"
                value={stepType}
                onChange={(e) => setStepType(e.target.value)}
              >
                <option value="" disabled></option>
                {contentTypes.map((contentType) => (
                  <option key={contentType.id} value={contentType.name}>
                    {capitalizeFirstLetter(contentType.name)}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="mb-4">
            <label htmlFor="file" className="block mb-2 text-base font-medium ml-2">
              {t.file}
            </label>
            <div className="relative">
              <input
                type="file"
                id="file"
                className="hidden"
                onChange={(e) =>
                  handleFileChange(e, setStepFile, setMainFileUrl)
                }
              />
              <label
                htmlFor="file"
                className="w-full p-8 border-2 border-dashed rounded-lg shadow-sm cursor-pointer flex justify-center items-center dark:bg-darkBackground hover:border-purple dark:hover:border-purple transition-all"
                style={{ borderColor: stepFile ? '#8B5CF6' : '' }}
                onDragEnter={(e) => {
                  setDragTarget("mainFile");
                  handleDrag(e);
                }}
                onDragOver={(e) => {
                  setDragTarget("mainFile");
                  handleDragOver(e);
                }}
                onDrop={handleDrop}
              >
                {stepFile ? (
                  <div className="relative w-full flex items-center justify-center">
                    {mainFileUrl ? (
                      <>
                        <img
                          src={mainFileUrl}
                          alt="Main file preview"
                          className="max-h-48 rounded-lg shadow-md"
                        />
                        <button
                          type="button"
                          className="absolute -top-3 -right-3 p-2 bg-white dark:bg-darkSecondaryBackground rounded-full shadow-lg hover:bg-red-50 dark:hover:bg-red-900 dark:hover:bg-opacity-20 transition-all"
                          onClick={(e) =>
                            handleRemoveFile(setStepFile, setMainFileUrl, e)
                          }
                        >
                          <FaTimes className="text-red-500 hover:scale-110 transition-transform" />
                        </button>
                      </>
                    ) : (
                      <>
                        <svg className="w-10 h-10 text-purple mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="text-center text-gray-700 dark:text-gray-300 font-medium">{stepFile.name}</p>
                        <button
                          type="button"
                          className="absolute right-0 top-1/2 transform -translate-y-1/2 mr-2 p-2 hover:bg-red-50 dark:hover:bg-red-900 dark:hover:bg-opacity-20 rounded-lg transition-all"
                          onClick={(e) =>
                            handleRemoveFile(setStepFile, setMainFileUrl, e)
                          }
                        >
                          <FaTimes className="text-red-500 hover:scale-110 transition-transform" />
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <svg className="mx-auto h-12 w-12 text-gray-400 mb-3" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                      <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <p className="text-center text-gray-600 dark:text-gray-400">{t.uploadFile}</p>
                  </div>
                )}
              </label>
            </div>
          </div>

          {stepType === "object" && (
            <div className="mb-4">
              <label
                htmlFor="secondaryFile"
                className="block mb-2 text-base font-medium ml-2"
              >
                USDZ {t.file}
              </label>
              <div className="relative">
                <input
                  type="file"
                  id="secondaryFile"
                  className="hidden"
                  onChange={(e) =>
                    handleFileChange(e, setSecondaryFile, setSecondaryFileUrl)
                  }
                />
                <label
                  htmlFor="secondaryFile"
                  className="w-full p-8 border-2 border-dashed rounded-lg shadow-sm cursor-pointer flex justify-center items-center dark:bg-darkBackground hover:border-purple dark:hover:border-purple transition-all"
                  style={{ borderColor: secondaryFile ? '#8B5CF6' : '' }}
                >
                  {secondaryFile ? (
                    <div className="relative w-full flex items-center justify-center">
                      {secondaryFileUrl ? (
                        <>
                          <img
                            src={secondaryFileUrl}
                            alt="Secondary file preview"
                            className="max-h-48 rounded-lg shadow-md"
                          />
                          <button
                            type="button"
                            className="absolute -top-3 -right-3 p-2 bg-white dark:bg-darkSecondaryBackground rounded-full shadow-lg hover:bg-red-50 dark:hover:bg-red-900 dark:hover:bg-opacity-20 transition-all"
                            onClick={(e) =>
                              handleRemoveFile(
                                setSecondaryFile,
                                setSecondaryFileUrl,
                                e
                              )
                            }
                          >
                            <FaTimes className="text-red-500 hover:scale-110 transition-transform" />
                          </button>
                        </>
                      ) : (
                        <>
                          <svg className="w-10 h-10 text-purple mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <p className="text-center text-gray-700 dark:text-gray-300 font-medium">{secondaryFile.name}</p>
                          <button
                            type="button"
                            className="absolute right-0 top-1/2 transform -translate-y-1/2 mr-2 p-2 hover:bg-red-50 dark:hover:bg-red-900 dark:hover:bg-opacity-20 rounded-lg transition-all"
                            onClick={(e) =>
                              handleRemoveFile(
                                setSecondaryFile,
                                setSecondaryFileUrl,
                                e
                              )
                            }
                          >
                            <FaTimes className="text-red-500 hover:scale-110 transition-transform" />
                          </button>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <svg className="mx-auto h-12 w-12 text-gray-400 mb-3" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                        <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <p className="text-center text-gray-600 dark:text-gray-400">{t.uploadFile}</p>
                    </div>
                  )}
                </label>
                <p className="text-red-500 font-semibold text-sm mt-2 ml-2">
                  {t2.animation_note}
                </p>
              </div>
            </div>
          )}

          {stepType === "object" && (
            <RotationInput
              rotation={stepRotation}
              setRotation={setStepRotation}
              label={t.rotation}
            />
          )}
          {stepType === "panorama" && (
            <PanoramaInputs
              clickToPaths={stepClickToPaths}
              setClickToPaths={setStepClickToPaths}
              links={links || []}
            />
          )}

          <div className="mb-20 relative">
            <label htmlFor="thumbnail" className="block mb-2 text-base font-medium ml-2">
              {t.thumbnail}
            </label>
            <div
              className={`w-full p-8 border-2 border-dashed rounded-lg shadow-sm dark:bg-darkBackground transition-all ${
                dragActive ? "border-purple bg-purple bg-opacity-5" : thumbnailUrl || thumbnail ? "border-purple" : "border-gray-300 hover:border-purple dark:hover:border-purple"
              }`}
              onDragEnter={(e) => {
                setDragTarget("thumbnail");
                handleDrag(e);
              }}
              onDragOver={(e) => {
                setDragTarget("thumbnail");
                handleDragOver(e);
              }}
              onDrop={handleDrop}
            >
              <input
                type="file"
                id="thumbnail"
                className="hidden"
                onChange={(e) =>
                  handleFileChange(e, setThumbnail, setThumbnailUrl)
                }
              />
              <label
                htmlFor="thumbnail"
                className="w-full h-full flex justify-center items-center cursor-pointer relative"
              >
                {thumbnailUrl ? (
                  <div className="relative">
                    <img
                      src={thumbnailUrl}
                      alt="Thumbnail"
                      className="max-h-48 rounded-lg shadow-md"
                    />
                    <button
                      type="button"
                      className="absolute -top-3 -right-3 p-2 bg-white dark:bg-darkSecondaryBackground rounded-full shadow-lg hover:bg-red-50 dark:hover:bg-red-900 dark:hover:bg-opacity-20 transition-all"
                      onClick={(e) =>
                        handleRemoveFile(setThumbnail, setThumbnailUrl, e, true)
                      }
                    >
                      <FaTimes className="text-red-500 hover:scale-110 transition-transform" />
                    </button>
                  </div>
                ) : thumbnail ? (
                  <div className="relative flex items-center">
                    <svg className="w-10 h-10 text-purple mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-center text-gray-700 dark:text-gray-300 font-medium">{thumbnail.name}</p>
                    <button
                      type="button"
                      className="absolute -right-12 p-2 hover:bg-red-50 dark:hover:bg-red-900 dark:hover:bg-opacity-20 rounded-lg transition-all"
                      onClick={(e) =>
                        handleRemoveFile(setThumbnail, setThumbnailUrl, e, true)
                      }
                    >
                      <FaTimes className="text-red-500 hover:scale-110 transition-transform" />
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <svg className="mx-auto h-12 w-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-center text-gray-600 dark:text-gray-400 font-medium">{t.thumbnailDrop}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">or click to browse</p>
                  </div>
                )}
              </label>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              className="bg-gray-300 text-gray-700 px-6 py-2.5 rounded-lg font-medium shadow-sm hover:bg-opacity-90 transition-all dark:bg-white dark:text-black"
              onClick={() => navigate(-1)}
            >
              {t.discard}
            </button>
            <button
              type="submit"
              className="bg-purple text-white px-6 py-2.5 rounded-lg font-medium shadow-sm hover:bg-opacity-90 transition-all"
              form="add-steps-form"
            >
              {t.save}
            </button>
          </div>
        </form>
      </div>
      <AddLanguageModal
        isOpen={isStepNameModalOpen}
        onClose={() => setIsStepNameModalOpen(false)}
        translations={t}
        mode="stepName"
        values={stepNames}
        setValues={setStepNames}
      />
    </div>
  );
};

export default EditStep;
